import { getWebsiteIdFromPath } from './util/websiteIdExtractor';

export class AutoWiseNotification {
    /**
     * @type {string}
     */
    #urlToServiceWorker = '/adminNotificationServiceWorker.js';

    /**
     * @type {boolean}
     */
    #isPushEnabled = false;

    /**
     * @type {boolean}
     */
    #isSubscriptionRequestInProgress = false;

    /**
     * @type {HTMLButtonElement}
     */
    #subscriptionButton;

    /**
     * @type {HTMLButtonElement}
     */
    #dismissButton;

    /**
     * @type {HTMLElement}
     */
    #consentBar;

    /**
     * @type {String}
     */
    #applicationServerKey;

    /**
     * @type {String}
     */
    #subscriptionEndpoint;

    /**
     * @type {HTMLButtonElement}
     */
    #reactivatePushButton;

    constructor(applicationServerKey, subscriptionEndpoint) {
        this.#consentBar = document.getElementById('notification-consent-bar');
        this.#dismissButton = document.getElementById('dismiss-notification-consent');
        this.#subscriptionButton = document.getElementById('push-subscription-button');
        this.#reactivatePushButton = document.getElementById('reactivate-push-notifications');
        this.#applicationServerKey = applicationServerKey;
        this.#subscriptionEndpoint = subscriptionEndpoint;

        if (this.#consentBar === null || this.#subscriptionButton === null) {
            console.debug('AutoWise Notification: The consent bar or the subscription button is missing. Skipping setup.');

            return;
        }

        this.setupConsentBar();
        this.setupPushNotifications();
    }

    async setupConsentBar() {
        if (!this.isPushNotificationsSupported()) {
            return;
        }

        if (await this.#isAlreadyRegisteredButIsMissingInLocalStorage()) {
            try {
                await this.#checkNotificationPermission(false);
                await this.setupPushNotifications();
                this.dismissConsentBar();
            } catch {
                console.info('User probably cancelled the notification permission request.');
                localStorage.removeItem('notification-consent-dismissed');
                return;
            }
        }

        this.#consentBar.classList.add('translate-y-full');

        if (!localStorage.getItem('notification-consent-dismissed')) {
            setTimeout(() => {
                this.#consentBar.classList.remove('hidden');
            }, 250);

            setTimeout(() => {
                this.#consentBar.classList.remove('translate-y-full');
            }, 1000);
        }

        if (this.#dismissButton) {
            this.#dismissButton.addEventListener('click', () => this.dismissConsentBar());
        }

        if (this.#subscriptionButton) {
            this.#subscriptionButton.addEventListener('click', async () => {
                await this.subscribeForPushNotifications();
                this.dismissConsentBar();
            });
        }
    }

    dismissConsentBar() {
        this.#consentBar.classList.remove('translate-y-0');
        this.#consentBar.classList.add('translate-y-full');

        if (!localStorage.getItem('notification-consent-dismissed')) {
            localStorage.setItem('notification-consent-dismissed', new Date().toISOString());
        }
    }

    async setupPushNotifications() {
        if (!this.isPushNotificationsSupported()) {
            return;
        }

        if (this.#reactivatePushButton) {
            this.#reactivatePushButton.addEventListener('click', async () => {
                const state = this.#reactivatePushButton.dataset.state;

                if (state === 'enabled') {
                    await this.#cancelSubscription();
                    localStorage.removeItem('notification-consent-dismissed');
                    window.location.reload();
                }

                if (state === 'disabled') {
                    await this.subscribeForPushNotifications();
                }
            });
        }

        try {
            await this.#checkNotificationPermission(false);
        } catch {
            this.#changePushButtonState('disabled');
            return;
        }

        navigator.serviceWorker
            .register(this.#urlToServiceWorker, {
                type: 'module',
                updateViaCache: 'none',
            })
            .then(
                async (serviceWorker) => {
                    await serviceWorker.update();
                    await this.#updateSubscription();
                },
                (error) => {
                    console.error('Notification | Service worker registration failed', error);
                },
            );
    }

    isPushNotificationsSupported() {
        return (
            'serviceWorker' in navigator &&
            'PushManager' in window &&
            'showNotification' in ServiceWorkerRegistration.prototype &&
            'Notification' in window &&
            Notification.permission !== 'denied'
        );
    }

    async subscribeForPushNotifications() {
        this.#changePushButtonState('computing');

        try {
            await this.#checkNotificationPermission(true);
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.#urlBase64ToUint8Array(this.#applicationServerKey),
            });

            await this.#sendSubscriptionToServer(subscription, 'POST');
            this.#changePushButtonState('enabled');

            console.debug('Push notifications are enabled.');
        } catch (error) {
            if (Notification.permission === 'denied') {
                console.debug('Push notifications are blocked by the user.');
                this.#changePushButtonState('incompatible');
            } else {
                console.error('Impossible to subscribe to push notifications', error);
                await this.#changePushButtonState('disabled');
            }
        }
    }

    async #updateSubscription() {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            this.#changePushButtonState('disabled');

            if (!subscription) {
                return;
            }

            await this.#sendSubscriptionToServer(subscription, 'PUT');
            this.#changePushButtonState('enabled');

            console.debug('Notification | Subscription updated.');
        } catch (error) {
            console.error('Notification | Error when updating the subscription', error);
        }
    }

    async #sendSubscriptionToServer(subscription, method) {
        if (this.#isSubscriptionRequestInProgress) {
            return;
        }

        this.#isSubscriptionRequestInProgress = true;
        const key = subscription.getKey('p256dh');
        const token = subscription.getKey('auth');
        const contentEncoding = (PushManager.supportedContentEncodings || ['aesgcm'])[0];

        try {
            await fetch(this.#subscriptionEndpoint, {
                method,
                body: JSON.stringify({
                    endpoint: subscription.endpoint,
                    publicKey: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null,
                    authToken: token ? btoa(String.fromCharCode.apply(null, new Uint8Array(token))) : null,
                    contentEncoding,
                }),
            });
        } finally {
            this.#isSubscriptionRequestInProgress = false;
        }
    }

    #checkNotificationPermission(request = true) {
        return new Promise((resolve, reject) => {
            if (Notification.permission === 'denied') {
                return reject(new Error('Push messages are blocked.'));
            }

            if (Notification.permission === 'granted') {
                return resolve();
            }

            if (request && Notification.permission === 'default') {
                return Notification.requestPermission().then((result) => {
                    if (result !== 'granted') {
                        reject(new Error('Bad permission result'));
                    } else {
                        resolve();
                    }
                });
            }

            return reject(new Error('Unknown permission'));
        });
    }

    #changePushButtonState(state) {
        if (this.#reactivatePushButton) {
            this.#reactivatePushButton.querySelector('.undetermined').setAttribute('hidden', '');
            this.#reactivatePushButton.querySelector('.activate').setAttribute('hidden', '');
            this.#reactivatePushButton.querySelector('.deactivate').setAttribute('hidden', '');
        }

        if (state === 'computing') {
            this.#reactivatePushButton.querySelector('.undetermined').attributes.removeNamedItem('hidden');
        }

        if (state === 'enabled') {
            this.#isPushEnabled = true;

            if (this.#reactivatePushButton) {
                this.#reactivatePushButton.querySelector('.deactivate').attributes.removeNamedItem('hidden');
            }
        }

        if (state === 'disabled') {
            this.#subscriptionButton.disabled = false;
            this.#isPushEnabled = false;

            if (this.#reactivatePushButton) {
                this.#reactivatePushButton.querySelector('.deactivate').attributes.removeNamedItem('hidden');
            }
        }

        if (this.#reactivatePushButton) {
            this.#reactivatePushButton.dataset.state = state;
        }
    }

    #urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    async #isAlreadyRegisteredButIsMissingInLocalStorage() {
        const serviceWorkerRegistration = await navigator.serviceWorker.ready;

        return serviceWorkerRegistration.active.scriptURL.endsWith(this.#urlToServiceWorker);
    }

    async #cancelSubscription() {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await this.#sendSubscriptionToServer(subscription, 'DELETE');
            }

            if (registration) {
                await registration.unregister();
            }

            this.#changePushButtonState('disabled');
            console.debug('Push notifications subscription cancelled.');
        } catch (error) {
            console.error('Error when cancelling push notification subscription:', error);
            this.#changePushButtonState('disabled');
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new AutoWiseNotification(import.meta.env.APP_VAPID_PUBLIC_KEY, `/${getWebsiteIdFromPath()}/notification/web-push/subscription`);
});
